/**
 * CPU の判断（docs/03_simulation.md 11 章）。
 * 予報と見えている雲・雷雲から天気を推定し、「歩き通す」か「どの離陸地から、どこへ飛ぶか」を選ぶ。
 */
import { LAUNCH, PACK, TIME } from '../../config/balance';
import type { Personality } from '../../config/rivals';
import { angleDiff } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { LaunchLevel, NodeData, NodeKind } from '../../core/world';
import { edgeTimeS, type HikeModifiers } from '../athlete/hike';
import {
  flatDownFactor,
  prepPackFactor,
  sinkFactor,
  staminaSpeedFactor,
  uphillFactor,
} from '../athlete/stats';
import { maxLaunchWindKmh } from '../flight/launch';
import { trimGlideRatio } from '../flight/physics';
import type { Course } from '../race/progress';
import type { AthleteState, PlanStep } from '../types';
import { windForecastAt, type Forecast } from '../weather/forecast';
import type { WeatherModel } from '../weather/model';
import { thermalRatio } from '../weather/truth';
import type { Graph } from '../world/graph';
import { shortestTimes } from '../world/pathfind';

export interface BrainContext {
  graph: Graph;
  course: Course;
  forecast: Forecast;
  weather: WeatherModel;
  timeOfDayS: number;
  raceTimeS: number;
  rng: Rng;
  personality: Personality;
}

export interface Decision {
  steps: PlanStep[];
  reason: string;
}

const LANDING_KINDS = new Set<NodeKind>(['valley', 'start', 'checkpoint', 'goal', 'temp', 'launchLow']);
/** サーマルを使ったときの平均の対地速度（上昇の時間を含む） */
const XC_SPEED_MS = 7;
/** サーマルをつないで狙いどおりに届く見込み（サーマルの強さが最大のとき） */
const THERMAL_SUCCESS: Record<LaunchLevel, number> = { peak: 0.7, ridge: 0.4, low: 0.15 };

export function hikeModsFor(a: AthleteState): HikeModifiers {
  return {
    uphill: uphillFactor(a.stats),
    flatDown: flatDownFactor(a.stats),
    overall: staminaSpeedFactor(a.stamina),
  };
}

/** デバッグ用：最後の判断で除外された理由の集計と候補（画面なしの検証ツールで読む） */
export const brainDebug = {
  rejected: {} as Record<string, number>,
  options: [] as { reason: string; score: number; detail?: string }[],
};

export function decidePlan(a: AthleteState, ctx: BrainContext): Decision | null {
  brainDebug.rejected = {};
  const reject = (why: string): void => {
    brainDebug.rejected[why] = (brainDebug.rejected[why] ?? 0) + 1;
  };
  if (a.finishTimeS !== null || a.nodeId === null) return null;
  const { graph, course, forecast, weather, rng, personality: pers } = ctx;
  const cpNode = course.nodeIds[Math.min(a.nextCp, course.nodeIds.length - 1)] as number;
  const from = a.nodeId;
  const mods = hikeModsFor(a);
  const cost = (e: Parameters<typeof edgeTimeS>[0], fwd: boolean): number => edgeTimeS(e, fwd, mods);
  const timeFrom = shortestTimes(graph, from, cost);
  const timeToCp = shortestTimes(graph, cpNode, cost, true);
  const now = ctx.timeOfDayS;
  const here = graph.nodes[from] as NodeData;

  // 見えている天気の推定（メンタルが低いほど読み違える）
  const sigma = (0.25 * (10 - a.stats.mental)) / 9;
  const climb = forecast.thermal.maxClimbMs * (1 + rng.range(-sigma, sigma));
  let stormChance = forecast.storm.chance;
  let stormArea = forecast.storm.area;
  let stormFromH = forecast.storm.fromH;
  const towering = weather.state.clouds.find(
    (c) => c.towering > 0.3 && Math.hypot(c.x - here.pos.x, c.z - here.pos.z) < 25_000,
  );
  if (towering) {
    stormChance = Math.max(stormChance, 0.85);
    stormArea = { x: towering.x, z: towering.z, radiusM: 9000, label: '' };
    stormFromH = Math.min(stormFromH, now / 3600 + 0.5);
  }
  for (const s of weather.state.storms) {
    if (s.phase === 'mature') {
      stormChance = 1;
      stormArea = { x: s.x, z: s.z, radiusM: s.radiusM + 6000, label: '' };
      stormFromH = now / 3600;
    }
  }

  type Option = Decision & { score: number; detail?: string };
  const options: Option[] = [];
  const baseline = timeFrom[cpNode] as number;
  if (
    Number.isFinite(baseline) &&
    pers.forceTier !== 'low' &&
    pers.forceTier !== 'ridge' &&
    pers.forceTier !== 'peak'
  ) {
    options.push({
      steps: [{ kind: 'hike', to: cpNode }],
      reason: 'hikeOnly',
      score: baseline - pers.hikeBias * 1800,
    });
  }

  if (pers.forceTier !== 'none') {
    const ld = trimGlideRatio(sinkFactor(a.stats)) * 0.85;
    const prepS = LAUNCH.prepS * prepPackFactor(a.stats);
    const packS = PACK.normalS * prepPackFactor(a.stats);
    const maxWind = maxLaunchWindKmh(a.stats);
    const styleK = pers.autoStyle === 'attack' ? 1.2 : 0.9;
    for (const L of graph.nodes) {
      if (!L.launch) continue;
      if (pers.forceTier && L.launch.level !== pers.forceTier) continue;
      if (a.blockedLaunches.some((b) => b.nodeId === L.id && b.untilS > ctx.raceTimeS)) continue;
      const tL = timeFrom[L.id] as number;
      if (!Number.isFinite(tL) || tL > 8 * 3600) {
        reject('far');
        continue;
      }
      const launchT = now + tL + prepS;
      if (launchT > TIME.sunsetS - 1800) {
        reject('late');
        continue;
      }

      // 予報の風で離陸できそうか
      const wf = windForecastAt(forecast, launchT / 3600, L.launch.level);
      if (wf.kmh > maxWind) {
        reject('wind');
        continue;
      }
      // 追い風・横風でも離陸はできる（失敗して数分ロスしやすい）ので、少し減点するだけ
      const fromRad = (wf.fromDeg * Math.PI) / 180;
      const maxHead = (LAUNCH.maxHeadAngleDeg * Math.PI) / 180;
      const tailwind =
        wf.kmh >= LAUNCH.calmKmh && !L.launch.facings.some((d) => angleDiff(fromRad, d) <= maxHead);

      const r = thermalRatio(forecast.thermal, launchT);
      // サーマルによる上乗せ：低い離陸地ではまずつながらない。稜線は半分、山頂は満額
      const tierK = L.launch.level === 'peak' ? 1 : L.launch.level === 'ridge' ? 0.5 : 0;
      const bonusM = climb * r * 5000 * styleK * tierK;

      // 届く着陸地のうち、CP までの残りの歩きが最も短いもの。滑空距離は着陸地との実際の標高差で見積もる
      let best: NodeData | null = null;
      let bestRest = Infinity;
      let bestGlide = 0;
      // サーマルがつながらなかったときに滑空だけで降りる先（CP までの残りの歩きが最も短いもの）
      let fall: NodeData | null = null;
      let fallRest = Infinity;
      for (const T of graph.nodes) {
        if (!LANDING_KINDS.has(T.kind) || T.id === L.id) continue;
        const d = Math.hypot(T.pos.x - L.pos.x, T.pos.z - L.pos.z);
        let glide = Math.max(0, L.pos.y - T.pos.y - 150) * ld;
        // 途中の尾根を滑空で越えられなければ、サーマル頼み
        if (glide >= d) {
          for (let k = 1; k < 8; k++) {
            const x = L.pos.x + ((T.pos.x - L.pos.x) * k) / 8;
            const z = L.pos.z + ((T.pos.z - L.pos.z) * k) / 8;
            if (L.pos.y - (d * k) / 8 / ld < weather.terrainHeight(x, z) + 80) {
              glide = 0;
              break;
            }
          }
        }
        const rest = T.id === cpNode ? 0 : (timeToCp[T.id] as number);
        if (d <= glide && rest < fallRest) {
          fallRest = rest;
          fall = T;
        }
        if (d > glide + bonusM) continue;
        if (rest < bestRest) {
          bestRest = rest;
          best = T;
          bestGlide = glide;
        }
      }
      if (!best || !Number.isFinite(bestRest)) {
        reject('noTarget');
        continue;
      }
      const d = Math.hypot(best.pos.x - L.pos.x, best.pos.z - L.pos.z);
      const glideM = bestGlide;
      const flightS = d <= glideM ? d / 10 : d / XC_SPEED_MS;
      // サーマル頼みなら、つながらなかったとき（滑空だけで降りる、降りる先がなければ離陸地から歩く）も見込む
      const need = d > glideM ? (d - glideM) / Math.max(bonusM, 1) : 0;
      const pOk = d > glideM ? THERMAL_SUCCESS[L.launch.level] * r * (1 - 0.5 * Math.min(need, 1)) : 1;
      const failS = fall
        ? Math.hypot(fall.pos.x - L.pos.x, fall.pos.z - L.pos.z) / 10 + fallRest
        : (timeToCp[L.id] as number) - packS;
      const eta = tL + prepS + packS + pOk * (flightS + bestRest) + (1 - pOk) * failS;

      // リスク：サーマル頼みの距離、雷雨、スタミナ
      let penalty = tailwind ? 600 : 0;
      if (d > glideM) penalty += pers.riskAversion * (1 - pOk) * 1800;
      if (stormChance > 0 && stormArea) {
        const startH = launchT / 3600;
        const endH = (launchT + flightS) / 3600;
        const overlap = endH > stormFromH - 0.5 && startH < stormFromH + 2.5;
        const nearL = Math.hypot(L.pos.x - stormArea.x, L.pos.z - stormArea.z) < stormArea.radiusM + 5000;
        const nearT =
          Math.hypot(best.pos.x - stormArea.x, best.pos.z - stormArea.z) < stormArea.radiusM + 5000;
        if (overlap && (nearL || nearT))
          penalty += stormChance * 3600 * pers.stormCaution * (0.5 + pers.riskAversion);
      }
      const climbNeeded = Math.max(0, L.pos.y - here.pos.y);
      const staminaCost = (climbNeeded / 100) * 1.5 + (tL / 3600) * 6;
      if (a.stamina - staminaCost < pers.staminaReserve) penalty += 3600;

      const bias = L.launch.level === 'peak' ? pers.peakBias * 1800 : 0;
      const steps: PlanStep[] = [];
      if (L.id !== from) steps.push({ kind: 'hike', to: L.id });
      steps.push({ kind: 'flight', targetNodeId: best.id });
      if (best.id !== cpNode) steps.push({ kind: 'hike', to: cpNode });
      const reason =
        L.launch.level === 'peak' ? 'peakHunt' : L.launch.level === 'ridge' ? 'ridgeLaunch' : 'lowLaunch';
      options.push({
        steps,
        reason,
        score: eta + penalty - bias,
        detail: `L${L.id}(${L.launch.level}) tL=${(tL / 3600).toFixed(1)}h →T${best.id} d=${(d / 1000).toFixed(1)}km glide=${(glideM / 1000).toFixed(1)}km bonus=${(bonusM / 1000).toFixed(1)}km pOk=${pOk.toFixed(2)} rest=${(bestRest / 3600).toFixed(1)}h fail=${(failS / 3600).toFixed(1)}h eta=${(eta / 3600).toFixed(1)}h pen=${(penalty / 3600).toFixed(1)}h`,
      });
    }
  }
  // 離陸地の種類を固定したテスト用の選手も、飛べる候補がなければ歩く
  if (options.length === 0 && Number.isFinite(baseline)) {
    options.push({ steps: [{ kind: 'hike', to: cpNode }], reason: 'hikeOnly', score: baseline });
  }
  brainDebug.options = options.map((o) => ({
    reason: o.reason,
    score: Math.round(o.score),
    detail: o.detail,
  }));
  if (options.length === 0) return null;

  // 得点によるソフトマックス（メンタルが低いほどブレる）
  const temp = 0.05 + (0.3 * (10 - a.stats.mental)) / 9;
  const minScore = Math.min(...options.map((o) => o.score));
  const weights = options.map((o) => Math.exp(-(o.score - minScore) / 3600 / temp));
  const total = weights.reduce((s, w) => s + w, 0);
  let u = rng.next() * total;
  for (let i = 0; i < options.length; i++) {
    u -= weights[i] as number;
    if (u <= 0) {
      const o = options[i] as Option;
      const hikeWasCheaper = o.reason === 'hikeOnly' && stormChance > 0.5;
      return { steps: o.steps, reason: hikeWasCheaper ? 'avoidStorm' : o.reason };
    }
  }
  const last = options[options.length - 1] as Option;
  return { steps: last.steps, reason: last.reason };
}
